import forOwn from 'lodash/forOwn'
import type { FormikConfig, FormikProps } from 'formik'
import { Formik, Form as FormikForm } from 'formik'
import { useError } from '@ztechapps/common.hook'
import type { FormikErrors, FormikHelpers } from 'formik/dist/types'

export type FormErrors<T> = { [K in keyof T]: string }

export enum FormStatus {
  initial = 'initial',
  success = 'success',
  error = 'error',
}

export type FormActionFunction<TValues = any> = (
  values: TValues,
  formikHelpers: FormikHelpers<TValues>
) => void | Promise<any>
export type FormActionCallbackFunction<TValues = any> = (
  values: TValues,
  formikBag: FormikProps<TValues>
) => void | Promise<any>
export type FormValidateFunction<TValues = any> = (values: TValues) => void | object | Promise<FormikErrors<TValues>>

export type FormProps<TValues = any> = FormikConfig<TValues> & {
  afterSubmit?: FormActionCallbackFunction<TValues>
  afterReset?: FormActionCallbackFunction<TValues>
}

export function Form<TValues = any>({
  children,
  onSubmit,
  onReset,
  afterSubmit,
  afterReset,
  ...formikProps
}: FormProps<TValues>) {
  const { onError } = useError()

  const onSubmitForm = async (values, formikBag) => {
    try {
      formikBag.setStatus(FormStatus.initial)
      if (onSubmit) {
        const result = await onSubmit?.(values, formikBag)

        //throw if there are some errors
        const errors = result?.errors

        if (errors) throw errors
      }

      ///submission was done so set the formik status
      formikBag.setStatus(FormStatus.success)

      //set the after submit
      afterSubmit?.(values, formikBag)
    } catch (e) {
      console.debug(e, 'Error when submitting the form.')
      //set the error status
      formikBag.setStatus(FormStatus.error)

      let errorMessage = e
      //get the error message here, and we are just interested with the first item of the message
      const validationError = e?.validationError
      if (validationError) {
        errorMessage = 'Please fix the validation errors on the form.'
        //if there is an error set the error for that fields
        //loop thru all error fields
        forOwn(validationError, (value, key) => {
          formikBag?.setFieldError(key, value)
        })
      }
      onError(errorMessage)
    }
  }

  const onResetForm = async (values, formikBag) => {
    if (onReset) {
      await onReset(values, formikBag)
    }

    afterReset?.(values, formikBag)
  }

  const useFormikContext = typeof children !== 'function'
  const allFormikProps = {
    ...formikProps,
    onSubmit: onSubmitForm,
    onReset: onResetForm,
  }

  if (useFormikContext) {
    return (
      <Formik {...allFormikProps}>
        <FormikForm noValidate={true}>{children}</FormikForm>
      </Formik>
    )
  }

  const childFunc = children as unknown as (props: FormikProps<any>) => any

  return (
    <Formik {...allFormikProps}>
      {(formikProps) => <FormikForm noValidate={true}>{childFunc(formikProps)}</FormikForm>}
    </Formik>
  )
}
